import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { ChartOptions } from "chart.js";
import { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef, ForwardedRef } from "react";
import { Line } from "react-chartjs-2";
import { useLocation, useNavigate } from "react-router-dom";
import { APIContext } from "../../Contexts/APIContext";
import { SiteReturnLabel, navigateToSite } from "../../Site/SiteNavigation";
import Directions from "../Directions";
import {
    Chart as ChartJS,
    CategoryScale,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import { ITripRateChartData } from "../../TripRate/ViewModels/ExportTripRateLineGraphViewModel";
import { TripRateSitesGraphDirection } from "./TripRateSitesGraphTypes";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface TripRateSitesViewModel {
    label: string;
    data: Array<number | null>;
    borderColor: string;
    backgroundColor?: string | null;
}

const hourlyLabels = Array.from({ length: 24 }, (_, i) => (i + 1).toString());
const halfHourlyLabels = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? "00" : "30";
    return `${hour.toString().padStart(2, "0")}:${minute}`;
});

const graphHeight = 640;
const legendPadding = 12;
const legendRowHeight = 22;
const legendColumnGap = 18;
const legendMarkerSize = 8;
const maximumVisibleLegendColumns = 4;

interface LegendHitArea {
    datasetIndex: number;
    x: number;
    y: number;
    width: number;
    height: number;
}

function createZeroDatasets(surveyRefs: string[]): TripRateSitesViewModel[] {
    const dataLength = surveyRefs[0]?.split("-")[1] === "02" ? 48 : 24;

    return surveyRefs.map((surveyRef, index) => {
        const color = `hsl(${(360 * index) / surveyRefs.length}, 65%, 55%)`;

        return {
            label: surveyRef.split("|")[0],
            data: Array(dataLength).fill(0),
            borderColor: color,
            backgroundColor: color,
        };
    });
}

function getNonZeroRange(datasets: TripRateSitesViewModel[]) {
    if (!datasets.length) return [0, 0];

    const dataLength = datasets[0].data.length;
    let first = 0;
    let last = Math.max(dataLength - 1, 0);

    for (let i = 0; i < dataLength; i++) {
        if (datasets.some(ds => ds.data[i] && ds.data[i] !== 0)) {
            first = i;
            break;
        }
    }

    for (let i = dataLength - 1; i >= 0; i--) {
        if (datasets.some(ds => ds.data[i] && ds.data[i] !== 0)) {
            last = i;
            break;
        }
    }

    return [first, last];
}

function isNearLine(x: number, y: number, linePoints: Array<{ x: number; y: number }>, tolerance = 5) {
    for (let i = 0; i < linePoints.length - 1; i++) {
        const p1 = linePoints[i];
        const p2 = linePoints[i + 1];
        const A = x - p1.x;
        const B = y - p1.y;
        const C = p2.x - p1.x;
        const D = p2.y - p1.y;

        const dot = A * C + B * D;
        const lenSq = C * C + D * D;
        const param = lenSq !== 0 ? dot / lenSq : -1;

        let xx: number;
        let yy: number;
        if (param < 0) {
            xx = p1.x;
            yy = p1.y;
        } else if (param > 1) {
            xx = p2.x;
            yy = p2.y;
        } else {
            xx = p1.x + param * C;
            yy = p1.y + param * D;
        }

        const dx = x - xx;
        const dy = y - yy;
        if (Math.sqrt(dx * dx + dy * dy) < tolerance) {
            return true;
        }
    }

    return false;
}

export function getTripRateSitesGraphTitle(direction: TripRateSitesGraphDirection, vehicleTypeLabel?: string) {
    const directionLabel = direction === "Total" ? "Totals" : direction;
    return `Trip Rate Line Graph for Selected Sites - ${vehicleTypeLabel ? `${vehicleTypeLabel} - ` : ""}${directionLabel}`;
}

function getTripRateYAxisTitle(vehicleTypeLabel?: string) {
    if (!vehicleTypeLabel) return "Trip Rates";

    const singularModeLabel = vehicleTypeLabel
        .replace(/\bVehicles$/, "Vehicle")
        .replace(/\bPassengers$/, "Passenger")
        .replace(/\bOccupants$/, "Occupant")
        .replace(/\bPedestrians$/, "Pedestrian")
        .replace(/\bCyclists$/, "Cyclist")
        .replace(/\bScooters$/, "Scooter")
        .replace(/\bMotorcycles$/, "Motorcycle")
        .replace(/\bCars$/, "Car")
        .replace(/\bTaxis$/, "Taxi")
        .replace(/\bUsers$/, "User")
        .replace(/\b(LGV|PSV|OGV)s$/, "$1");

    return `${singularModeLabel} Trip Rates`;
}

interface IProps {
    surveyRefs: string[];
    headerIndex: number;
    vehicleType: number;
    vehicleTypeLabel?: string;
    direction: TripRateSitesGraphDirection;
    origin: "surveySelection" | "tripRateCalculation" | "rankOrder";
    returnLabel: SiteReturnLabel;
    returnTo: string;
    height?: number;
    onChartDataReady?: (data: ITripRateChartData | null) => void;
    onDirectionChange: (direction: TripRateSitesGraphDirection) => void;
}

export interface TripRateSitesGraphHandle {
    exportToJPEG: (fileName: string) => void;
}

const TripRateSitesGraph = forwardRef<TripRateSitesGraphHandle, IProps>(
    ({
        surveyRefs,
        headerIndex,
        vehicleType,
        vehicleTypeLabel,
        direction,
        origin,
        returnLabel,
        returnTo,
        height = graphHeight,
        onChartDataReady,
        onDirectionChange,
    }, ref: ForwardedRef<TripRateSitesGraphHandle>) => {
        const { post } = useContext(APIContext);
        const navigate = useNavigate();
        const location = useLocation();
        const chartRef = useRef<ChartJS<"line">>(null);
        const legendCanvasRef = useRef<HTMLCanvasElement>(null);
        const legendViewportRef = useRef<HTMLDivElement>(null);
        const legendHitAreasRef = useRef<LegendHitArea[]>([]);
        const postRef = useRef(post);
        const [status, setStatus] = useState<"loading" | "loaded" | "empty">("loading");
        const [datasets, setDatasets] = useState<TripRateSitesViewModel[]>([]);

        const siteKey = useMemo(() => surveyRefs.join("|"), [surveyRefs]);

        useEffect(() => {
            postRef.current = post;
        }, [post]);

        useEffect(() => {
            let active = true;

            if (!surveyRefs.length) {
                setDatasets([]);
                setStatus("empty");
                onChartDataReady?.(null);
                return;
            }

            setStatus("loading");
            onChartDataReady?.(null);

            postRef.current(
                "Chart",
                `GetSelectedSurveysTripRateChart?headerIndex=${headerIndex}&direction=${direction}&vehicleType=${vehicleType}`,
                surveyRefs,
                (data: TripRateSitesViewModel[]) => {
                    if (!active) return;

                    const next = data ?? [];
                    setDatasets(next.length > 0 ? next : createZeroDatasets(surveyRefs));
                    setStatus("loaded");
                }
            );

            return () => {
                active = false;
            };
        }, [headerIndex, direction, vehicleType, siteKey]);

        const landUseCode = datasets[0]?.label?.split("-")[1] || surveyRefs[0]?.split("-")[1] || "00";
        const allLabels = landUseCode === "02" ? halfHourlyLabels : hourlyLabels;
        const yAxisTitle = getTripRateYAxisTitle(vehicleTypeLabel);

        const [trimmedLabels, trimmedDatasets] = useMemo(() => {
            const [firstIndex, lastIndex] = getNonZeroRange(datasets);
            return [
                allLabels.slice(firstIndex, lastIndex + 1),
                datasets.map(ds => ({
                    ...ds,
                    data: Array.isArray(ds.data) ? ds.data.slice(firstIndex, lastIndex + 1) : []
                }))
            ];
        }, [datasets, allLabels]);

        const chartData = useMemo<ITripRateChartData>(() => ({
            title: getTripRateSitesGraphTitle(direction, vehicleTypeLabel),
            xAxisTitle: "Time (24 Hour)",
            yAxisTitle,
            labels: trimmedLabels,
            datasets: trimmedDatasets as ITripRateChartData["datasets"],
        }), [direction, vehicleTypeLabel, yAxisTitle, trimmedLabels, trimmedDatasets]);

        useEffect(() => {
            if (status === "loaded") {
                onChartDataReady?.(chartData);
            } else {
                onChartDataReady?.(null);
            }
        }, [chartData, onChartDataReady, status]);

        const drawLegend = useCallback(() => {
            const chart = chartRef.current;
            const canvas = legendCanvasRef.current;
            if (!chart || !canvas) return;

            const font = "12px Arial";
            const measurementContext = canvas.getContext("2d");
            if (!measurementContext) return;

            measurementContext.font = font;
            const itemWidths = chart.data.datasets.map(dataset =>
                legendMarkerSize + 6 + measurementContext.measureText(dataset.label ?? "").width
            );
            const cssHeight = Math.max(1, height - 16);
            const rowsPerColumn = Math.max(1, Math.floor((cssHeight - legendPadding * 2) / legendRowHeight));
            const columnCount = Math.ceil(itemWidths.length / rowsPerColumn);
            const columnWidths = Array.from({ length: columnCount }, (_, columnIndex) => {
                const firstItemIndex = columnIndex * rowsPerColumn;
                return Math.max(...itemWidths.slice(firstItemIndex, firstItemIndex + rowsPerColumn), 0);
            });
            const cssWidth = Math.max(1, Math.ceil(
                legendPadding * 2
                + columnWidths.reduce((total, width) => total + width, 0)
                + Math.max(columnWidths.length - 1, 0) * legendColumnGap
            ));
            const visibleColumnWidths = columnWidths.slice(0, maximumVisibleLegendColumns);
            const viewportWidth = Math.max(1, Math.ceil(
                legendPadding * 2
                + visibleColumnWidths.reduce((total, width) => total + width, 0)
                + Math.max(visibleColumnWidths.length - 1, 0) * legendColumnGap
            ));
            const pixelRatio = window.devicePixelRatio || 1;

            if (legendViewportRef.current) {
                legendViewportRef.current.style.width = `${viewportWidth}px`;
                legendViewportRef.current.style.flexBasis = `${viewportWidth}px`;
            }

            canvas.style.width = `${cssWidth}px`;
            canvas.style.height = `${cssHeight}px`;
            canvas.width = Math.ceil(cssWidth * pixelRatio);
            canvas.height = Math.ceil(cssHeight * pixelRatio);

            const context = canvas.getContext("2d");
            if (!context) return;

            context.scale(pixelRatio, pixelRatio);
            context.clearRect(0, 0, cssWidth, cssHeight);
            context.font = font;
            context.textBaseline = "middle";

            const columnOffsets: number[] = [];
            let nextColumnX = legendPadding;
            columnWidths.forEach(columnWidth => {
                columnOffsets.push(nextColumnX);
                nextColumnX += columnWidth + legendColumnGap;
            });

            legendHitAreasRef.current = chart.data.datasets.map((dataset, datasetIndex) => {
                const columnIndex = Math.floor(datasetIndex / rowsPerColumn);
                const rowIndex = datasetIndex % rowsPerColumn;
                const itemsInColumn = Math.min(
                    rowsPerColumn,
                    chart.data.datasets.length - columnIndex * rowsPerColumn
                );
                const columnTop = Math.max(
                    legendPadding,
                    (cssHeight - itemsInColumn * legendRowHeight) / 2
                );
                const x = columnOffsets[columnIndex];
                const y = columnTop + rowIndex * legendRowHeight;
                const centreY = y + legendRowHeight / 2;
                const itemWidth = itemWidths[datasetIndex];
                const visible = chart.isDatasetVisible(datasetIndex);
                const colour = typeof dataset.borderColor === "string" ? dataset.borderColor : "#666";
                const label = dataset.label ?? "";

                context.save();
                context.globalAlpha = visible ? 1 : 0.45;
                context.fillStyle = colour;
                context.beginPath();
                context.arc(x + legendMarkerSize / 2, centreY, legendMarkerSize / 2, 0, Math.PI * 2);
                context.fill();

                const textX = x + legendMarkerSize + 6;
                context.fillStyle = "#666";
                context.fillText(label, textX, centreY);

                if (!visible) {
                    const textWidth = context.measureText(label).width;
                    context.strokeStyle = "#666";
                    context.lineWidth = 1;
                    context.beginPath();
                    context.moveTo(textX, centreY);
                    context.lineTo(textX + textWidth, centreY);
                    context.stroke();
                }
                context.restore();

                const hitArea = { datasetIndex, x, y, width: itemWidth, height: legendRowHeight };
                return hitArea;
            });
        }, [height]);

        useEffect(() => {
            if (status !== "loaded") return;

            // Allow react-chartjs-2 to finish applying the latest data before measuring the legend.
            const frame = window.requestAnimationFrame(drawLegend);
            return () => window.cancelAnimationFrame(frame);
        }, [drawLegend, chartData, status]);

        const handleLegendClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
            const chart = chartRef.current;
            const canvas = legendCanvasRef.current;
            if (!chart || !canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const hitArea = legendHitAreasRef.current.find(area =>
                x >= area.x && x <= area.x + area.width
                && y >= area.y && y <= area.y + area.height
            );
            if (!hitArea) return;

            chart.setDatasetVisibility(hitArea.datasetIndex, !chart.isDatasetVisible(hitArea.datasetIndex));
            chart.update("none");
            drawLegend();
        };

        useImperativeHandle(ref, () => ({
            exportToJPEG: (fileName: string) => {
                if (!chartRef.current) return;

                const sourceCanvas = chartRef.current.canvas as HTMLCanvasElement | undefined;
                const legendCanvas = legendCanvasRef.current;
                if (!sourceCanvas || !legendCanvas) return;

                const exportCanvas = document.createElement("canvas");
                const pixelRatio = window.devicePixelRatio || 1;
                const exportGap = Math.ceil(8 * pixelRatio);
                exportCanvas.width = sourceCanvas.width + exportGap + legendCanvas.width;
                exportCanvas.height = Math.max(sourceCanvas.height, legendCanvas.height);
                const exportCtx = exportCanvas.getContext("2d");
                if (!exportCtx) return;

                exportCtx.fillStyle = "#ffffff";
                exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
                exportCtx.drawImage(sourceCanvas, 0, 0);
                exportCtx.drawImage(legendCanvas, sourceCanvas.width + exportGap, 0);

                const link = document.createElement("a");
                link.href = exportCanvas.toDataURL("image/jpeg", 1.0);
                link.download = `${fileName}.jpeg`;
                link.click();
            }
        }));

        const options: ChartOptions<"line"> = useMemo(() => ({
            responsive: true,
            normalized: true,
            animation: false,
            maintainAspectRatio: false,
            transitions: {
                active: { animation: { duration: 0 } },
                show: { animation: { duration: 0 } },
                hide: { animation: { duration: 0 } },
                resize: { animation: { duration: 0 } },
            },
            onClick: (event, elements, chart) => {
                if (elements.length > 0) {
                    const datasetIndex = elements[0].datasetIndex;
                    const label = chart.data.datasets[datasetIndex].label;
                    navigateToSite(navigate, location, {
                        siteRef: label ?? "",
                        type: origin === "rankOrder" ? "rankorder" : "summaryFiltering",
                        fromTab: origin === "rankOrder" ? "rankGraph" : "surveySummary",
                        returnLabel,
                        returnTo,
                    });
                    return;
                }

                const mouseEvent = event.native as MouseEvent;
                const canvas = chart.canvas;
                const rect = canvas.getBoundingClientRect();
                const x = mouseEvent.clientX - rect.left;
                const y = mouseEvent.clientY - rect.top;

                for (let i = 0; i < chart.data.datasets.length; i++) {
                    const datasetMeta = chart.getDatasetMeta(i);
                    const points = datasetMeta.data.map((point: any) => ({
                        x: point.x,
                        y: point.y,
                    }));

                    if (isNearLine(x, y, points, 5)) {
                        const label = chart.data.datasets[i].label;
                        navigateToSite(navigate, location, {
                            siteRef: label ?? "",
                            type: origin === "rankOrder" ? "rankorder" : "summaryFiltering",
                            fromTab: origin === "rankOrder" ? "rankGraph" : "surveySummary",
                            returnLabel,
                            returnTo,
                        });
                        return;
                    }
                }
            },
            plugins: {
                title: {
                    display: true,
                    text: getTripRateSitesGraphTitle(direction, vehicleTypeLabel),
                },
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    title: { display: true, text: "Time (24 Hour)" },
                },
                y: {
                    beginAtZero: true,
                    title: { display: true, text: yAxisTitle },
                },
            },
            elements: {
                line: { borderWidth: 1 },
                point: {
                    radius: 1.5,
                    hoverRadius: 3,
                }
            }
        }), [direction, vehicleTypeLabel, yAxisTitle, navigate, location, origin, returnLabel, returnTo]);

        const panelSx = {
            width: "100%",
            minWidth: 0,
            height,
            minHeight: height,
            position: "relative" as const,
        };

        return (
            <Box sx={{ width: "100%" }}>
                <Directions setType={(type) => onDirectionChange(type as TripRateSitesGraphDirection)} type={direction} />
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box sx={panelSx}>
                        {status === "loading" && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 2,
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <CircularProgress size={32} />
                                <Typography variant="body2">Calculating graph...</Typography>
                            </Box>
                        )}

                        {status === "empty" && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography variant="body2">No graph data is available for the selected mode and surveys.</Typography>
                            </Box>
                        )}

                        {status === "loaded" && (
                            <Box sx={{ display: "flex", width: "100%", height: "100%", minWidth: 0 }}>
                                <Box sx={{ flex: "1 1 auto", height: "100%", minWidth: 0 }}>
                                    <Line
                                        ref={chartRef}
                                        data={chartData}
                                        options={options}
                                    />
                                </Box>
                                <Box
                                    ref={legendViewportRef}
                                    sx={{
                                        flex: "0 0 auto",
                                        minWidth: 0,
                                        height: "100%",
                                        overflowX: "auto",
                                        overflowY: "hidden",
                                    }}
                                >
                                    <canvas
                                        ref={legendCanvasRef}
                                        onClick={handleLegendClick}
                                        aria-label="Site references. Click a site reference to show or hide it on the graph."
                                        role="img"
                                        style={{ display: "block", cursor: "pointer" }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }
);

export default TripRateSitesGraph;
